import { useFragment } from '@apollo/client/react';
import { capitalize } from 'lodash';
import { startTransition } from 'react';

import { createRequiredContext } from 'src/context/createRequiredContext';
import {
  EscrowPartyTypeEnum,
  EscrowStatusEnum,
  IBeneficiary,
  IDepositor,
  InvitationStatusEnum,
} from 'src/graphql/schema';
import { getEscrowStatus, setTrackToStatus } from 'src/utils/escrow';
import { OtherEscrowStatusEnum } from 'src/shared/constants';

import { SoftwareEscrowDashboardFragment } from '../api';

import type { ISoftwareEscrowsQuery } from 'src/graphql/actions/softwareEscrow/softwareEscrows.query';

import { useDashboard } from './DashboardContext';

const [useDashboardItem, DashboardItemProvider] = createRequiredContext<{
  id: string;
  agreementForm: string;
  status: string;
  previousStatus: string | null;
  parties: {
    id: string;
    name: string;
    logo: string;
    shouldFetchLogo: boolean;
    isNew: boolean;
    partyType: EscrowPartyTypeEnum;
  }[];
  invitationId?: string;
  activatedAt: string;
  certificationVerified: boolean;
  certificationCertified: boolean;
  lastDeposit: string;
  userUpdatedAt: string;
  createdAt: string;
  changesRequested: boolean;
  escrowTermination: boolean;
  pendingActivation: boolean;
  onRemove: (e: React.MouseEvent<HTMLButtonElement>) => void;
  escrowInvitationRejected: boolean;
}>();

const DashboardItemContextProvider = ({ id: idEscrow, children }: React.PropsWithChildren<{ id: string }>) => {
  const { setRemoveDialogOpen } = useDashboard();
  const { data: dataEscrow, complete } = useFragment<ISoftwareEscrowsQuery['softwareEscrows']['nodes'][0]>({
    fragment: SoftwareEscrowDashboardFragment,
    from: {
      __typename: 'SoftwareEscrow',
      id: idEscrow,
    },
  });

  if (!complete) return null;

  const {
    id,
    status: escrowStatus,
    agreement,
    escrowTermination,
    escrowInvitation,
    activatedAt,
    certificationVerified,
    certificationCertified,
    lastDeposit,
    userUpdatedAt,
    createdAt,
    changesRequested,
    pendingActivation,
  } = dataEscrow;
  const parties = [
    {
      partyType: EscrowPartyTypeEnum.Depositor,
      data: dataEscrow.depositor as IDepositor,
    },
    {
      partyType: EscrowPartyTypeEnum.Beneficiary,
      data: dataEscrow.beneficiary as IBeneficiary,
    },
  ].map(({ partyType, data }) => getPartyData({ partyType, data, escrowInvitation }));
  const invitationId =
    escrowInvitation?.status === InvitationStatusEnum.Pending && escrowInvitation.policy.accept
      ? escrowInvitation.id
      : undefined;
  const status = getEscrowStatus({
    escrowStatus,
    escrowAgreementApproved: Boolean(agreement?.approved),
    escrowTermination,
    pendingActivation,
  });
  const previousStatus = getPreviousStatus(status, dataEscrow);

  const onRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    startTransition(() => setRemoveDialogOpen(true, id));
  };

  const values = {
    id,
    agreementForm: capitalize(agreement!.agreementForm!),
    status,
    previousStatus,
    parties,
    invitationId,
    lastDeposit,
    userUpdatedAt,
    createdAt,
    activatedAt,
    certificationVerified,
    certificationCertified,
    changesRequested,
    escrowTermination: Boolean(escrowTermination),
    pendingActivation,
    onRemove,
    escrowInvitationRejected: escrowInvitation?.status === InvitationStatusEnum.Rejected,
  };

  return <DashboardItemProvider value={values}>{children}</DashboardItemProvider>;
};

function getPartyData({
  partyType,
  data,
  escrowInvitation,
}: {
  partyType: EscrowPartyTypeEnum;
  data: IDepositor | IBeneficiary;
  escrowInvitation: ISoftwareEscrowsQuery['softwareEscrows']['nodes'][0]['escrowInvitation'];
}) {
  return {
    id: data?.id ?? `new-${partyType}`,
    name: data?.companyName || escrowInvitation?.companyName || capitalize(partyType),
    logo: data?.companyWebsite ?? '',
    shouldFetchLogo: Boolean(data?.companyWebsite),
    isNew: !data?.id,
    partyType,
  };
}

function getPreviousStatus(
  status: EscrowStatusEnum | OtherEscrowStatusEnum,
  softwareEscrow: ISoftwareEscrowsQuery['softwareEscrows']['nodes'][0],
) {
  const previousStatusMap = {
    [OtherEscrowStatusEnum.TerminationRequested]: softwareEscrow.status,
    [EscrowStatusEnum.PendingTermination]: setTrackToStatus({
      createdAt: softwareEscrow.createdAt,
      adminReviewAt: softwareEscrow.adminReviewAt,
      onboardingAt: softwareEscrow.onboardingAt,
      signedAt: softwareEscrow.signedAt,
      activatedAt: softwareEscrow.activatedAt,
    }),
  };

  return previousStatusMap[status as keyof typeof previousStatusMap] ?? null;
}

export { DashboardItemContextProvider, useDashboardItem };
